import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuArrowDownLeft, LuArrowUpRight, LuPackageOpen, LuTrash2, LuTriangleAlert, LuTruck, LuUserCog, LuWallet } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import FilterTiles from '../../../components/data/FilterTiles';
import useElementWidth from '../../../hooks/useElementWidth';
import { db } from '../../../config/firebaseConfig';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { SHIPMENT_STATUS } from '../../../utils/shipments';
import {
  CARGO_DIRECTION,
  InsufficientStockError,
  isCargoOverdue,
  matchesCargoSearch,
  summarizeCompanyShipments,
} from '../../../utils/companyShipments';
import { CARD, EmptyState, HelpNote, LoadingRows, SearchField, SectionHeader } from '../../ProfilePage/components/ProfileUi';
import useCompanyShipments from './useCompanyShipments';
import useCargoText from './useCargoText';
import CargoList from './CargoList';
import CargoFormModal from './CargoFormModal';
import CargoDetailDrawer from './CargoDetailDrawer';
import CargoAccessModal from './CargoAccessModal';

// Özet kartları aynı zamanda hızlı filtredir
const QUICK_FILTERS = {
  all: () => true,
  incoming: (shipment) => shipment.direction === CARGO_DIRECTION.INCOMING,
  outgoing: (shipment) => shipment.direction !== CARGO_DIRECTION.INCOMING,
  inTransit: (shipment) => shipment.status === SHIPMENT_STATUS.SHIPPED,
  overdue: isCargoOverdue,
};

// Bu genişlikten itibaren ayrıntılar listenin yanında açılır
const DOCK_MIN_WIDTH = 1100;

/**
 * Firma kargo takibi.
 *  - mode="company"  : Stok Yönetimi sekmesi — tüm kayıtlar + yetki yönetimi
 *  - mode="personal" : Profil sekmesi — yalnızca kullanıcının kendi kargoları
 */
const CargoTracking = ({ mode = 'company' }) => {
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const { language, label, statusText, formatCost } = useCargoText();
  const isPersonal = mode === 'personal';
  const isAdmin =
    userAuthorization?.includes('admin') || userAuthorization?.includes('Kısıtlı Admin');
  const actorName = `${employeeData?.eName || ''} ${employeeData?.eSurname || ''}`.trim();

  const { shipments, loading, error, saveShipment, changeStatus, removeShipment } =
    useCompanyShipments({ ownerId: isPersonal ? employeeId : null, actorName });

  const [stocks, setStocks] = useState([]);
  const [quickFilter, setQuickFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [formState, setFormState] = useState(null); // { shipment, direction } | null
  const [showAccess, setShowAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Onay penceresi kapanırken metni kaybolmasın
  const lastDeleteTarget = useRef(null);
  if (deleteTarget) lastDeleteTarget.current = deleteTarget;
  const deleteShown = deleteTarget || lastDeleteTarget.current;

  const rootRef = useRef(null);
  const docked = useElementWidth(rootRef) >= DOCK_MIN_WIDTH;

  // Stok kalemi seçimi için aktif stok kartları. Form her açıldığında okunur;
  // böylece "kalan" miktarlar kargo hareketlerinden sonra da günceldir.
  const formOpen = Boolean(formState);
  useEffect(() => {
    if (!formOpen) return undefined;
    let cancelled = false;
    getDocs(collection(db, 'Stocks'))
      .then((snapshot) => {
        if (cancelled) return;
        const options = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((stock) => stock['Durum'] !== 'inaktif')
          .map((stock) => ({
            id: stock.id,
            name: stock['Adı'] || 'İsimsiz stok',
            remaining: parseFloat(stock['Kalan']) || 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setStocks(options);
      })
      .catch((stockError) => console.error('Stok listesi yüklenemedi:', stockError));
    return () => {
      cancelled = true;
    };
  }, [formOpen]);

  const summary = useMemo(() => summarizeCompanyShipments(shipments), [shipments]);
  const visible = useMemo(
    () =>
      shipments.filter(
        (shipment) => QUICK_FILTERS[quickFilter](shipment) && matchesCargoSearch(shipment, search)
      ),
    [shipments, quickFilter, search]
  );
  const selected = useMemo(
    () => shipments.find((shipment) => shipment.id === selectedId) || null,
    [shipments, selectedId]
  );

  const reportError = (actionError, fallback) => {
    console.error(fallback, actionError);
    toast.error(actionError instanceof InsufficientStockError ? actionError.message : fallback);
  };

  const handleSave = async (formData) => {
    try {
      await saveShipment(formState.shipment, formData, { id: employeeId, name: actorName });
      toast.success(formState.shipment ? label('toast.updated') : label('toast.added'));
    } catch (saveError) {
      reportError(saveError, label('toast.saveError'));
      throw saveError;
    }
  };

  const handleStatusChange = async (shipment, status) => {
    setBusy(true);
    try {
      await changeStatus(shipment, status);
      toast.success(label('toast.statusChanged', { status: statusText(shipment.direction, status).label }));
    } catch (statusError) {
      reportError(statusError, label('toast.statusError'));
    } finally {
      setBusy(false);
    }
  };

  // Silme onayı ConfirmDialog ile alınır; onaylanınca kayıt silinir
  const handleDelete = (shipment) => setDeleteTarget(shipment);

  const confirmDelete = async () => {
    const shipment = deleteTarget;
    setBusy(true);
    try {
      await removeShipment(shipment);
      setSelectedId(null);
      toast.success(label('toast.deleted'));
    } catch (deleteError) {
      reportError(deleteError, label('toast.deleteError'));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const closeForm = useCallback(() => setFormState(null), []);
  const closeDrawer = useCallback(() => setSelectedId(null), []);

  const totalCost = formatCost(summary.totalCost);
  const stats = [
    { key: 'all', value: summary.total, icon: LuPackageOpen, tone: 'neutral' },
    { key: 'incoming', value: summary.incoming, icon: LuArrowDownLeft, tone: 'success' },
    { key: 'outgoing', value: summary.outgoing, icon: LuArrowUpRight, tone: 'info' },
    { key: 'inTransit', value: summary.inTransit, icon: LuTruck, tone: 'warning' },
    { key: 'overdue', value: summary.overdue, icon: LuTriangleAlert, tone: 'danger', highlight: summary.overdue > 0 },
  ].map((stat) => ({ ...stat, label: label(`stats.${stat.key}`) }));
  stats.push({
    key: 'cost',
    static: true,
    compact: true,
    value: totalCost,
    title: totalCost,
    icon: LuWallet,
    tone: 'violet',
    label: label('stats.cost'),
    shortLabel: label('stats.costShort'),
  });

  const actions = (
    <div className="tw-grid tw-w-full tw-grid-cols-2 tw-gap-2 sm:tw-flex sm:tw-w-auto sm:tw-flex-wrap sm:tw-items-center">
      {!isPersonal && isAdmin && (
        <Button variant="ghost" onClick={() => setShowAccess(true)} className="tw-col-span-2 max-sm:tw-order-last">
          <LuUserCog aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('actions.access')}
        </Button>
      )}
      <Button onClick={() => setFormState({ shipment: null, direction: CARGO_DIRECTION.INCOMING })}>
        <LuArrowDownLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('actions.receive')}
      </Button>
      <Button variant="primary" onClick={() => setFormState({ shipment: null, direction: CARGO_DIRECTION.OUTGOING })}>
        <LuArrowUpRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        {label('actions.send')}
      </Button>
    </div>
  );

  const noRecords = shipments.length === 0;

  return (
    <div ref={rootRef} lang={language} className="tw-space-y-4 tw-font-sans tw-antialiased">
      {isPersonal ? (
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
          <HelpNote className="lg:tw-flex-1">{label('helpPersonal')}</HelpNote>
          {actions}
        </div>
      ) : (
        <SectionHeader icon={LuTruck} gradient="tw-from-orange-400 tw-to-rose-500 tw-shadow-orange-500/30" title={label('titleCompany')} description={label('subtitleCompany')}>
          {actions}
        </SectionHeader>
      )}

      <FilterTiles
        tiles={stats}
        value={quickFilter}
        onChange={setQuickFilter}
        label={label('stats.label')}
        className="tw-grid-cols-3 lg:tw-grid-cols-6"
      />

      <div className="tw-flex tw-items-start tw-gap-4">
        <div className={`${CARD} tw-min-w-0 tw-flex-1 tw-space-y-3 tw-p-3 sm:tw-p-4`}>
          <div className="tw-flex tw-items-center tw-gap-3">
            <SearchField value={search} onChange={setSearch} placeholder={label('search')} className="tw-flex-1" />
            {!loading && !error && !noRecords && (
              <span className="tw-hidden tw-shrink-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-500 sm:tw-inline dark:tw-text-slate-400">
                {label('count', { count: visible.length })}
              </span>
            )}
          </div>

          {loading ? (
            <LoadingRows count={3} height="tw-h-[4.5rem]" label={label('loading')} />
          ) : error ? (
            <HelpNote tone="danger">
              <span role="alert">{label('loadError')}</span>
            </HelpNote>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={LuPackageOpen}
              title={noRecords ? label('empty.noneTitle') : label('empty.noMatchTitle')}
              text={noRecords ? label('empty.noneText') : label('empty.noMatchText')}
              action={!noRecords && (quickFilter !== 'all' || search) ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setQuickFilter('all');
                    setSearch('');
                  }}
                >
                  {label('empty.clearFilters')}
                </Button>
              ) : null}
            />
          ) : (
            <CargoList
              shipments={visible}
              showOwner={!isPersonal}
              selectedId={formState ? null : selectedId}
              onSelect={(shipment) => setSelectedId(shipment.id)}
            />
          )}
        </div>

        {docked && (
          <CargoDetailDrawer
            docked
            shipment={formState ? null : selected}
            canEdit
            busy={busy}
            onClose={closeDrawer}
            onEdit={(shipment) => setFormState({ shipment, direction: shipment.direction })}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>

      {!docked && (
        <CargoDetailDrawer
          shipment={formState ? null : selected}
          canEdit
          busy={busy}
          onClose={closeDrawer}
          onEdit={(shipment) => setFormState({ shipment, direction: shipment.direction })}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
        />
      )}
      <CargoFormModal
        show={Boolean(formState)}
        onHide={closeForm}
        shipment={formState?.shipment || null}
        initialDirection={formState?.direction}
        stocks={stocks}
        onSave={handleSave}
      />
      <CargoAccessModal show={showAccess} onHide={() => setShowAccess(false)} />
      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        tone="danger"
        icon={LuTrash2}
        title={label('confirmDelete.title')}
        message={deleteShown ? (
          <>
            {label('confirmDelete.message', { subject: deleteShown.subject })}
            {(deleteShown.appliedItems || []).length > 0 && (
              <span className="tw-mt-2 tw-block tw-text-sm tw-font-medium tw-text-amber-700 dark:tw-text-amber-300">{label('confirmDelete.stockNote')}</span>
            )}
          </>
        ) : ''}
        confirmLabel={label('confirmDelete.confirm')}
        cancelLabel={label('confirmDelete.cancel')}
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

CargoTracking.propTypes = {
  mode: PropTypes.oneOf(['company', 'personal']),
};

export default CargoTracking;
